import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { parseErrorMessage } from "@/lib/queryClient";
import { CheckCircle2, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { usePageTitle } from "@/hooks/use-page-title";

// Admin Access Layer — public completion page for the emailed single-use
// set-password link (/admin/setup-password?token=<id>.<secret>). Verifies the
// token, collects the new admin password, posts the completion, then sends
// the admin to /admin to sign in.

const MIN_PASSWORD_LENGTH = 12;

export default function AdminSetupPassword() {
  usePageTitle("Set Admin Password");
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get("token"));
  }, []);

  const verifyQuery = useQuery<{ valid: boolean; email: string }>({
    queryKey: ["/api/admin/password-setup/verify", token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const res = await fetch("/api/admin/password-setup/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
        credentials: "include",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/admin/password-setup/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
        credentials: "include",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      return res.json();
    },
    onSuccess: () => setCompleted(true),
    onError: (err) => {
      toast({
        title: "Could not set password",
        description: parseErrorMessage(err, "Something went wrong. Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast({
        title: "Password too short",
        description: `Use at least ${MIN_PASSWORD_LENGTH} characters.`,
        variant: "destructive",
      });
      return;
    }
    if (password !== confirm) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    completeMutation.mutate();
  };

  const invalidLink = !token || verifyQuery.isError;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm p-8">
        {completed ? (
          <div className="flex flex-col items-center text-center" data-testid="setup-password-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mb-4" />
            <h1 className="text-xl font-bold mb-2">Admin password set</h1>
            <p className="text-sm text-muted-foreground mb-6">
              You can now sign into the admin panel with your email and new password.
            </p>
            <Link href="/admin">
              <Button data-testid="button-go-to-admin-login">Go to admin sign-in</Button>
            </Link>
          </div>
        ) : invalidLink ? (
          <div className="flex flex-col items-center text-center" data-testid="setup-password-invalid">
            <ShieldAlert className="h-10 w-10 text-destructive mb-4" />
            <h1 className="text-xl font-bold mb-2">Link invalid or expired</h1>
            <p className="text-sm text-muted-foreground">
              This password setup link is no longer valid. Ask a super-admin to send you a new one.
            </p>
          </div>
        ) : verifyQuery.isLoading ? (
          <div className="flex flex-col items-center py-10" data-testid="setup-password-loading">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center text-center mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 mb-4">
                <KeyRound className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-xl font-bold" data-testid="text-setup-password-title">Set your admin password</h1>
              <p className="text-sm text-muted-foreground mt-1">
                For {verifyQuery.data?.email} — separate from your regular PadPulse password.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="new-admin-password">New admin password</Label>
                <Input
                  id="new-admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  data-testid="input-setup-password"
                />
                <p className="text-xs text-muted-foreground">At least {MIN_PASSWORD_LENGTH} characters.</p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-admin-password">Confirm password</Label>
                <Input
                  id="confirm-admin-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="input-setup-password-confirm"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={completeMutation.isPending || !password || !confirm}
                data-testid="button-setup-password-submit"
              >
                {completeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
